import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Sparkles, ClipboardEdit, User } from "lucide-react";

/**
 * Personal View tap menu — opens at the tap coordinate when a practitioner
 * taps one of their own appointment tiles. Two actions only: AI Summary
 * (routes into the existing AIBriefingModal) and Input Medical Record
 * (opens the per-visit medical-record dialog). Sized generously for
 * fat-finger use on iPad / phone.
 *
 * Iter-281 · Smart Menu Positioning: mirrors ContextMenu.jsx — flips the
 * menu upward when the tap lands in the bottom 40% of the viewport or the
 * appointment's local start (Asia/Makassar) is 15:00 or later, and clamps
 * every edge inside the viewport.
 */
export default function PersonalApptTapMenu({ x, y, appt, onClose, onAISummary, onMedicalRecord }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    // Delay attach so the click that opened the menu doesn't immediately
    // close it via the outside-click listener.
    const t = setTimeout(() => {
      window.addEventListener("mousedown", onDown);
      window.addEventListener("touchstart", onDown);
      window.addEventListener("keydown", onEsc);
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("touchstart", onDown);
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const width = 260;
  const [pos, setPos] = useState({ left: x, top: y, ready: false });

  const apptHour = (() => {
    try {
      const startIso = appt && (appt.start_iso || appt.start);
      if (!startIso) return 0;
      const s = new Date(startIso).toLocaleString("en-GB", {
        hour: "2-digit", hour12: false, timeZone: "Asia/Makassar",
      });
      return parseInt(s, 10) || 0;
    } catch { return 0; }
  })();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuH = rect.height || 220;
    const menuW = rect.width || width;
    const vh = (typeof window !== "undefined" ? window.innerHeight : 900);
    const vw = (typeof window !== "undefined" ? window.innerWidth : 1024);
    const pad = 8;

    const inBottomZone = y > vh * 0.6;
    const isLateAppt = apptHour >= 15;
    const preferUp = inBottomZone || isLateAppt;
    const roomAbove = y - pad;
    const roomBelow = vh - y - pad;

    let top;
    if (preferUp && roomAbove >= menuH) top = y - menuH;
    else if (!preferUp && roomBelow >= menuH) top = y;
    else if (roomAbove >= menuH) top = y - menuH;
    else if (roomBelow >= menuH) top = y;
    else top = pad;
    top = Math.max(pad, Math.min(top, vh - menuH - pad));

    let left = Math.min(x, vw - menuW - pad);
    left = Math.max(pad, left);

    setPos({ left, top, ready: true });
  }, [x, y, apptHour]);

  return (
    <div
      ref={ref}
      className="fixed z-50 rounded-xl bg-white border border-border shadow-xl overflow-hidden"
      style={{ left: pos.left, top: pos.top, width, visibility: pos.ready ? "visible" : "hidden" }}
      data-testid="personal-tap-menu"
      data-menu-direction={pos.ready ? (pos.top < y ? "up" : "down") : "pending"}
      role="menu"
    >
      <div className="px-4 py-3 bg-primary/5 border-b border-border">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
          <User className="w-3.5 h-3.5" />
          Personal view · Client
        </div>
        <div className="mt-1 text-sm font-semibold truncate" data-testid="personal-tap-customer">{appt?.customer_name || "Guest"}</div>
        {appt?.service_name && <div className="text-xs text-muted-foreground truncate">{appt.service_name}</div>}
      </div>
      <button
        type="button"
        onClick={onAISummary}
        className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-muted active:bg-muted transition-colors border-b border-border"
        data-testid="personal-tap-ai-summary"
        role="menuitem"
      >
        <Sparkles className="w-5 h-5 text-primary shrink-0" />
        <div className="min-w-0">
          <div className="font-medium text-sm">AI Summary</div>
          <div className="text-xs text-muted-foreground">Instant client briefing (medical + preferences)</div>
        </div>
      </button>
      <button
        type="button"
        onClick={onMedicalRecord}
        className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-muted active:bg-muted transition-colors"
        data-testid="personal-tap-medical-record"
        role="menuitem"
      >
        <ClipboardEdit className="w-5 h-5 text-primary shrink-0" />
        <div className="min-w-0">
          <div className="font-medium text-sm">Input Medical Record</div>
          <div className="text-xs text-muted-foreground">Log notes / treatments / follow-up for this visit</div>
        </div>
      </button>
    </div>
  );
}
